import React from 'react';
import { motion } from 'framer-motion';
import Button from '@mui/material/Button';
import { Leaf, Heart, Sparkles, Wind } from 'lucide-react';
import wellness1 from "../Images/wellness1.jpg";
import wellness2 from "../Images/wellness2.jpg";
import wellness3 from "../Images/wellness3.jpg";
import wellness4 from "../Images/wellness4.jpg";
import wellness5 from "../Images/wellness5.jpg";

export function WellnessSection() {
  const wellnessFeatures = [
    {
      icon: Leaf,
      title: 'Nature Immersion',
      description: 'Disconnect from the chaos and reconnect with pristine natural landscapes that heal and restore your spirit.',
      image: wellness4,
      color: 'from-emerald-500 to-teal-500',
    },
    {
      icon: Heart,
      title: 'Mindful Retreats',
      description: 'Experience guided meditation, yoga sessions, and mindfulness practices in serene environments designed for inner peace.',
      image: wellness3,
      color: 'from-blue-500 to-cyan-500',
    },
    {
      icon: Sparkles,
      title: 'Rejuvenation Programs',
      description: 'Holistic wellness programs combining spa treatments, healthy cuisine, and nature therapy for complete renewal.',
      image: wellness1,
      color: 'from-purple-500 to-pink-500',
    },
    {
      icon: Wind,
      title: 'Digital Detox',
      description: 'Reboot your mind with tech-free experiences in mountain retreats and coastal sanctuaries away from daily stress.',
      image: wellness5,
      color: 'from-indigo-500 to-blue-500',
    },
  ];

  return (
    <section className="relative py-24 bg-gradient-to-b from-teal-50 via-white to-emerald-50 overflow-hidden">
      {/* Decorative background elements */}
      <motion.div
        className="absolute top-0 left-0 w-full h-full opacity-30"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 0.3 }}
        viewport={{ once: true }}
      >
        <motion.div
          className="absolute top-20 left-10 w-64 h-64 bg-teal-200 rounded-full blur-3xl"
          animate={{
            scale: [1, 1.2, 1],
            x: [0, 30, 0],
            y: [0, -20, 0],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
        <motion.div
          className="absolute bottom-20 right-10 w-96 h-96 bg-emerald-200 rounded-full blur-3xl"
          animate={{
            scale: [1, 1.3, 1],
            x: [0, -40, 0],
            y: [0, 30, 0],
          }}
          transition={{
            duration: 25,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
      </motion.div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <motion.div
            initial={{ scale: 0, rotate: -180 }}
            whileInView={{ scale: 1, rotate: 0 }}
            viewport={{ once: true }}
            transition={{ type: "spring", stiffness: 200, delay: 0.2 }}
            className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-teal-500 to-emerald-500 text-white rounded-full mb-6"
          >
            <Leaf className="w-5 h-5" />
            <span>Your Wellness Journey Begins</span>
          </motion.div>
          
          <motion.h2
            className="mb-6 bg-gradient-to-r from-teal-700 to-emerald-700 bg-clip-text text-transparent"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
          >
            Leader in Wellness & Nature Tours
          </motion.h2>
          
          <motion.p
            className="text-gray-700 max-w-3xl mx-auto text-lg"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4 }}
          >
            Escape the ordinary and embrace transformation. Our curated wellness tours combine the healing power of nature with mindful practices, offering you a sanctuary to reboot, rejuvenate, and rediscover your inner peace.
          </motion.p>
        </motion.div>

        {/* Wellness Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
          {wellnessFeatures.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.15 }}
              whileHover={{ y: -8 }}
              className="group relative bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500"
            >
              {/* Image */}
              <div className="relative h-64 overflow-hidden">
                <motion.div
                  whileHover={{ scale: 1.1 }}
                  transition={{ duration: 0.6 }}
                  className="w-full h-full"
                >
                  <img 
                    src={feature.image} 
                    alt={feature.title} 
                    className="w-full h-full object-cover" 
                    loading="lazy"
                  />
                </motion.div>
                
                {/* Gradient overlay */}
                <div className={`absolute inset-0 bg-gradient-to-t ${feature.color} opacity-20 group-hover:opacity-30 transition-opacity duration-500`} />
                
                {/* Icon badge */}
                <motion.div
                  className="absolute top-6 right-6 w-14 h-14 bg-white rounded-full flex items-center justify-center shadow-lg"
                  whileHover={{ rotate: 360, scale: 1.1 }}
                  transition={{ duration: 0.6 }}
                >
                  <feature.icon className="w-7 h-7 text-teal-600" />
                </motion.div>
              </div>

              {/* Content */}
              <div className="p-8">
                <h3 className="mb-3 text-gray-900">{feature.title}</h3>
                <p className="text-gray-600 leading-relaxed">{feature.description}</p>

                {/* Animated underline */}
                <motion.div
                  className={`mt-6 h-1 bg-gradient-to-r ${feature.color} rounded-full`}
                  initial={{ width: 0 }}
                  whileInView={{ width: '60px' }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.15 + 0.5, duration: 0.6 }}
                />
              </div>
            </motion.div>
          ))}
        </div>

        {/* Stats Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="bg-gradient-to-r from-teal-600 to-emerald-600 rounded-3xl p-12 text-white"
        >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            {[
              { value: '95%', label: 'Satisfaction Rate' },
            
              { value: '50+', label: 'Nature Retreats' },
              { value: '4.9/5', label: 'Average Rating' },
            ].map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.5 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, type: "spring", stiffness: 200 }}
                whileHover={{ scale: 1.1 }}
                className="relative"
              >
                <motion.div
                  className="text-5xl mb-2"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 + 0.2 }}
                >
                  {stat.value}
                </motion.div>
                <div className="text-teal-100">{stat.label}</div>
                
                {/* Decorative element */}
                <motion.div
                  className="absolute -top-4 left-1/2 transform -translate-x-1/2 w-8 h-1 bg-white/30 rounded-full"
                  initial={{ width: 0 }}
                  whileInView={{ width: 32 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 + 0.4 }}
                />
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3, duration: 0.6 }}
          className="text-center mt-12"
        >
          <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
            <Button
              variant="contained"
              size="large"
              onClick={() => {
      document.getElementById('wellness-programs').scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
    }}
              sx={{
                px: 5,
                py: 2.5,
                borderRadius: '999px',
                background: 'linear-gradient(90deg, #0891b2, #059669)', 
                color: 'white',
                boxShadow: '0 10px 32px rgba(20,184,166,0.18)',
                fontWeight: 600,
                fontSize: '1.15rem',
                '&:hover': {
                  background: 'linear-gradient(90deg, #14b8a6, #10b981)',
                  boxShadow: '0 20px 40px rgba(20,184,166,0.36)',
                },
              }}
            >
              Explore Wellness Tours
            </Button>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
